import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { HorizontalBarList, MultiLineChart } from "@/components/charts";
import { useTraffic } from "@/hooks/useTraffic";
import { useHomepagePaths } from "@/hooks/useHomepagePaths";
import type { TimeRange } from "@/lib/time-range";

interface TrafficSectionProps {
  range: TimeRange;
}

function formatBucket(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

export function TrafficSection({ range }: TrafficSectionProps) {
  const traffic = useTraffic(range);
  const paths = useHomepagePaths(range);

  const totals = traffic.data?.totals;
  const series = traffic.data?.series;
  const trafficErr = traffic.error ? panelErrorTitle(traffic.error) : undefined;

  const returningRatio =
    totals && totals.visitors > 0
      ? `${((totals.returningVisitors / totals.visitors) * 100).toFixed(1)}% of visitors`
      : undefined;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Telemetry Traffic"
        frs={["FR-01", "FR-02"]}
        source={traffic.data?.source}
        description="SDK-instrumented funnel entry — home_view visitors feeding the reservation funnel below. Site-wide visitor totals live in GA4 Audience above."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricKpiTile
          label="Total visitors"
          value={totals ? totals.visitors.toLocaleString("en-US") : "—"}
          delta="All home_view events"
          tone="sky"
          hint="Total number of homepage loads in the selected range. Counts each visit, including return visits."
          isLoading={traffic.isLoading}
          errorTitle={trafficErr}
        />
        <MetricKpiTile
          label="Unique visitors"
          value={totals ? totals.uniqueVisitors.toLocaleString("en-US") : "—"}
          delta="Distinct ai.session.id"
          tone="neutral"
          hint="Distinct browser sessions that visited the site at least once."
          isLoading={traffic.isLoading}
          errorTitle={trafficErr}
        />
        <MetricKpiTile
          label="Returning visitors"
          value={totals ? totals.returningVisitors.toLocaleString("en-US") : "—"}
          delta={returningRatio}
          tone="emerald"
          hint="Visits from people who had already been on the site earlier in the lookback window."
          isLoading={traffic.isLoading}
          errorTitle={trafficErr}
        />
        <MetricKpiTile
          label="Peak page views"
          value={totals?.peakBucket?.value ?? "—"}
          delta={
            totals?.peakBucket
              ? `at ${formatBucket(totals.peakBucket.bucket)}`
              : "no peak in range"
          }
          tone="neutral"
          hint="The busiest single time bucket in the range — the size of the largest spike."
          isLoading={traffic.isLoading}
          errorTitle={trafficErr}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Visitors over time">
          {traffic.isLoading ? (
            <PanelLoading />
          ) : traffic.error ? (
            <PanelError
              error={traffic.error}
              onRetry={() => traffic.refetch()}
            />
          ) : (
            <MultiLineChart
              series={[
                { name: "Visitors", tone: "sky", points: series?.visitors ?? [] },
                { name: "Unique", tone: "emerald", points: series?.uniqueVisitors ?? [] },
                { name: "Returning", tone: "slate", points: series?.returningVisitors ?? [] },
              ]}
            />
          )}
        </Card>

        <Card title="Top downstream paths from /">
          {paths.isLoading ? (
            <PanelLoading />
          ) : paths.error ? (
            <PanelError error={paths.error} onRetry={() => paths.refetch()} />
          ) : (
            <HorizontalBarList
              entries={(paths.data ?? []).map((p) => ({
                label: p.path,
                value: p.entries,
              }))}
              tone="sky"
            />
          )}
        </Card>
      </div>
    </section>
  );
}
